/**
 * Contact Detail - Full CRM Contact View with 6 Tabs
 * Overview | Communications | Estimates | Documents | Activity | AI Insights
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Phone, Mail, MapPin, Calendar, Clock, DollarSign,
  MessageSquare, FileText, X, ChevronDown, Send, Sparkles,
  Trophy, Archive, Snowflake, Flame, Plus, User, Upload,
  Building, Target, AlertTriangle, RefreshCw, Loader2,
  ExternalLink, Download, Trash2, Brain, TrendingUp,
  CheckCircle, XCircle, Zap, History
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// ============== HELPERS ==============

const formatPhone = (phone) => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  }
  return phone;
};

const formatCurrency = (value) => {
  if (!value) return "-";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ============== MAIN COMPONENT ==============

const ContactDetail = ({
  contact,
  isOpen,
  onClose,
  onRefresh,
  onStageChange,
  onMarkWon,
  onMarkLost,
  onMoveToIcebox,
  onWakeFromIcebox,
  onDelete,
  canDelete,
  stages
}) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [fullContact, setFullContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wonValue, setWonValue] = useState("");
  
  // Communications state
  const [commType, setCommType] = useState("sms");
  const [commSubject, setCommSubject] = useState("");
  const [commBody, setCommBody] = useState("");
  const [sending, setSending] = useState(false);
  const [aiDrafting, setAiDrafting] = useState(false);
  
  // Documents state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // AI Insights state
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  
  // Notes state
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const notesTimeoutRef = useRef(null);

  // Load full contact data
  useEffect(() => {
    if (contact?.contact_id && isOpen) {
      loadFullContact();
    }
  }, [contact?.contact_id, isOpen]);

  const loadFullContact = async () => {
    try {
      setLoading(true);
      const response = await API.get(`/contacts/${contact.contact_id}`);
      setFullContact(response.data);
      setWonValue(response.data.contact?.estimated_value?.toString() || "");
      setNotes(response.data.contact?.notes || "");
    } catch (error) {
      toast.error("Failed to load contact details");
    } finally {
      setLoading(false);
    }
  };

  // Auto-save notes with debounce
  const saveNotes = useCallback(async (value) => {
    try {
      setSavingNotes(true);
      await API.patch(`/contacts/${contact.contact_id}/notes`, { notes: value });
    } catch (error) {
      console.error("Failed to save notes:", error);
    } finally {
      setSavingNotes(false);
    }
  }, [contact?.contact_id]);

  const handleNotesChange = (e) => {
    const value = e.target.value;
    setNotes(value);
    
    if (notesTimeoutRef.current) {
      clearTimeout(notesTimeoutRef.current);
    }
    notesTimeoutRef.current = setTimeout(() => {
      saveNotes(value);
    }, 1000);
  };

  // Send communication
  const handleSendCommunication = async () => {
    if (!commBody.trim()) {
      toast.error("Please enter a message");
      return;
    }
    
    setSending(true);
    try {
      await API.post(`/contacts/${contact.contact_id}/communications/send`, {
        type: commType,
        subject: commType === "email" ? commSubject : undefined,
        body: commBody
      });
      toast.success(`${commType.toUpperCase()} sent successfully`);
      setCommBody("");
      setCommSubject("");
      loadFullContact();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to send ${commType}`);
    } finally {
      setSending(false);
    }
  };

  // AI Draft message
  const handleAiDraft = async (context = "follow_up") => {
    setAiDrafting(true);
    try {
      const response = await API.post(`/contacts/${contact.contact_id}/ai/draft-message`, {
        type: commType,
        context
      });
      setCommBody(response.data.message);
      if (commType === "email" && response.data.subject) {
        setCommSubject(response.data.subject);
      }
      toast.success("AI draft generated");
    } catch (error) {
      toast.error("Failed to generate AI draft");
    } finally {
      setAiDrafting(false);
    }
  };

  // AI Re-score
  const handleRescore = async () => {
    setRescoring(true);
    try {
      await API.post(`/contacts/${contact.contact_id}/ai/rescore`);
      toast.success("Lead re-scored");
      loadFullContact();
    } catch (error) {
      toast.error("Failed to re-score lead");
    } finally {
      setRescoring(false);
    }
  };

  // Load AI Analysis
  const loadAiAnalysis = async () => {
    setLoadingAnalysis(true);
    try {
      const response = await API.get(`/contacts/${contact.contact_id}/ai/analysis`);
      setAiAnalysis(response.data);
    } catch (error) {
      toast.error("Failed to load AI analysis");
    } finally {
      setLoadingAnalysis(false);
    }
  };

  // Upload documents
  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    
    setUploading(true);
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    
    try {
      await API.post(`/contacts/${contact.contact_id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Documents uploaded");
      loadFullContact();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Delete document
  const handleDeleteDocument = async (docId) => {
    if (!confirm("Delete this document?")) return;
    try {
      await API.delete(`/contacts/${contact.contact_id}/documents/${docId}`);
      toast.success("Document deleted");
      loadFullContact();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  // Log manual communication
  const handleLogCommunication = async () => {
    if (!commBody.trim()) {
      toast.error("Please enter a note");
      return;
    }
    
    setSending(true);
    try {
      await API.post(`/contacts/${contact.contact_id}/communications`, {
        type: commType,
        direction: "outbound",
        body: commBody
      });
      toast.success("Communication logged");
      setCommBody("");
      loadFullContact();
      onRefresh();
    } catch (error) {
      toast.error("Failed to log communication");
    } finally {
      setSending(false);
    }
  };

  const handleMarkWon = async () => {
    const value = parseFloat(wonValue) || null;
    await onMarkWon(contact.contact_id, value, null);
  };

  if (!contact) return null;

  const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(" ");
  const address = [
    contact.address_street,
    contact.address_city,
    contact.address_state,
    contact.address_zip
  ].filter(Boolean).join(", ");
  const currentStage = stages?.find(s => s.key === contact.stage);
  const contactData = fullContact?.contact || contact;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 bg-slate-900 border-slate-700 overflow-hidden" data-testid="contact-detail-modal">
        {/* Accessibility - visually hidden title */}
        <DialogTitle className="sr-only">Contact Details - {fullName || 'Unknown'}</DialogTitle>
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-900/50">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
                <User className="h-6 w-6 text-slate-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{fullName || "Unknown Contact"}</h2>
                <div className="flex items-center gap-3 mt-1 text-sm text-slate-400">
                  {contact.phone && (
                    <a href={`tel:${contact.phone}`} className="flex items-center gap-1 hover:text-emerald-400">
                      <Phone className="h-3.5 w-3.5" />
                      {formatPhone(contact.phone)}
                    </a>
                  )}
                  {contact.email && (
                    <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-blue-400">
                      <Mail className="h-3.5 w-3.5" />
                      {contact.email}
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {contact.status === 'active' ? (
                <>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className={`px-3 py-1.5 rounded-lg font-medium text-sm flex items-center gap-2 ${currentStage?.color || 'bg-slate-700'} text-white`}>
                        {currentStage?.label || contact.stage}
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
                      {stages?.map(stage => (
                        <DropdownMenuItem
                          key={stage.key}
                          onClick={() => onStageChange(contact.contact_id, stage.key)}
                          className="cursor-pointer"
                        >
                          <span className={`w-2 h-2 rounded-full ${stage.color} mr-2`} />
                          {stage.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Button size="sm" variant="outline" className="border-cyan-600 text-cyan-400 hover:bg-cyan-900/30" onClick={() => onMoveToIcebox(contact)}>
                    <Snowflake className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-600 text-red-400 hover:bg-red-900/30" onClick={() => onMarkLost(contact)}>
                    <Archive className="h-4 w-4" />
                  </Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500" onClick={handleMarkWon}>
                    <Trophy className="h-4 w-4 mr-1" /> Won
                  </Button>
                  {canDelete && (
                    <Button size="sm" variant="outline" className="border-red-800 text-red-500 hover:bg-red-900/30" onClick={() => onDelete(contact)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge className={`${contact.status === 'won' ? 'bg-emerald-500/20 text-emerald-400' : ''} ${contact.status === 'lost' ? 'bg-red-500/20 text-red-400' : ''} ${contact.status === 'icebox' ? 'bg-cyan-500/20 text-cyan-400' : ''}`}>
                    {contact.status === 'icebox' && (
                      <button onClick={() => onWakeFromIcebox(contact.contact_id)} className="flex items-center gap-1">
                        <Snowflake className="h-3.5 w-3.5" /> Frozen
                        <Flame className="h-3.5 w-3.5 ml-1 hover:text-orange-400" title="Wake up" />
                      </button>
                    )}
                    {contact.status === 'won' && <><Trophy className="h-3.5 w-3.5 mr-1" /> Won</>}
                    {contact.status === 'lost' && <><Archive className="h-3.5 w-3.5 mr-1" /> Lost</>}
                  </Badge>
                  {canDelete && (
                    <Button size="sm" variant="outline" className="border-red-800 text-red-500 hover:bg-red-900/30" onClick={() => onDelete(contact)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-6 mt-4 bg-slate-800 border border-slate-700 grid grid-cols-6">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="communications" data-testid="tab-communications">Comms</TabsTrigger>
            <TabsTrigger value="estimates" data-testid="tab-estimates">Estimates</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai" onClick={() => !aiAnalysis && loadAiAnalysis()}>
              <Sparkles className="h-3.5 w-3.5 mr-1" /> AI
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 px-6 py-4">
            {/* ========== OVERVIEW TAB ========== */}
            <TabsContent value="overview" className="mt-0 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Contact Info */}
                <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 space-y-4">
                  <h3 className="font-medium text-slate-300 text-sm uppercase tracking-wider">Contact Info</h3>
                  {address && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-slate-500 mt-0.5" />
                      <span className="text-slate-300">{address}</span>
                    </div>
                  )}
                  {contactData.company && (
                    <div className="flex items-center gap-3">
                      <Building className="h-4 w-4 text-slate-500" />
                      <span className="text-slate-300">Company: {contactData.company}</span>
                    </div>
                  )}
                  {contactData.owns_land && contactData.owns_land !== "" && (
                    <div className="flex items-center gap-3">
                      <Target className="h-4 w-4 text-slate-500" />
                      <span className="text-slate-300">Owns Land: {contactData.owns_land}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Building className="h-4 w-4 text-slate-500" />
                    <span className="text-slate-300">Source: <Badge variant="outline" className="ml-1 border-slate-600">{contact.source?.replace(/_/g, " ")}</Badge></span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-slate-500" />
                    <span className="text-slate-400 text-sm">Created: {formatDate(contact.created_at)}</span>
                  </div>
                </div>

                {/* Service Request */}
                <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 space-y-4">
                  <h3 className="font-medium text-slate-300 text-sm uppercase tracking-wider">Service Request</h3>
                  <div>
                    <Label className="text-xs text-slate-500">Service Type</Label>
                    <p className="text-slate-200">{contact.service_type?.replace(/_/g, " ") || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Budget</Label>
                    <p className="text-slate-200">{contactData.budget || (contact.budget_min || contact.budget_max ? `${formatCurrency(contact.budget_min)} - ${formatCurrency(contact.budget_max)}` : "-")}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Timeline</Label>
                    <p className="text-slate-200">{contact.timeline?.replace(/_/g, " ") || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Estimated Value</Label>
                    <Input type="number" value={wonValue} onChange={(e) => setWonValue(e.target.value)} className="w-32 bg-slate-900 border-slate-700 mt-1" placeholder="$0" />
                  </div>
                </div>
              </div>

              {/* Description */}
              {contact.service_description && (
                <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                  <h3 className="font-medium text-slate-300 text-sm uppercase tracking-wider mb-2">Description</h3>
                  <p className="text-slate-300 whitespace-pre-wrap">{contact.service_description}</p>
                </div>
              )}

              {/* Message from Lead */}
              {contactData.message && contactData.message !== contact.service_description && (
                <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                  <h3 className="font-medium text-slate-300 text-sm uppercase tracking-wider mb-2">Message from Lead</h3>
                  <p className="text-slate-300 whitespace-pre-wrap">{contactData.message}</p>
                </div>
              )}

              {/* Notes */}
              <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-slate-300 text-sm uppercase tracking-wider">Notes</h3>
                  {savingNotes && <span className="text-xs text-slate-500">Saving...</span>}
                </div>
                <Textarea value={notes} onChange={handleNotesChange} placeholder="Add notes about this contact..." className="bg-slate-900 border-slate-700 min-h-[100px]" data-testid="contact-notes" />
              </div>

              {/* AI Score */}
              {contactData.ai_score !== undefined && (
                <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-slate-300 text-sm uppercase tracking-wider">AI Lead Score</h3>
                    <Button size="sm" variant="ghost" onClick={handleRescore} disabled={rescoring} className="text-slate-400 hover:text-white">
                      <RefreshCw className={`h-4 w-4 mr-1 ${rescoring ? 'animate-spin' : ''}`} />
                      {rescoring ? 'Scoring...' : 'Re-score'}
                    </Button>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className={`text-3xl font-bold ${contactData.ai_score >= 70 ? 'text-emerald-400' : contactData.ai_score >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                      {contactData.ai_score}
                    </div>
                    <div>
                      <Badge className={`${contactData.ai_score_label === 'high_intent' ? 'bg-emerald-500/20 text-emerald-400' : ''} ${contactData.ai_score_label === 'medium_intent' ? 'bg-amber-500/20 text-amber-400' : ''} ${contactData.ai_score_label === 'low_intent' ? 'bg-red-500/20 text-red-400' : ''}`}>
                        {contactData.ai_score_label?.replace(/_/g, " ")}
                      </Badge>
                      {contactData.ai_score_reasoning && <p className="text-xs text-slate-500 mt-1">{contactData.ai_score_reasoning}</p>}
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ========== COMMUNICATIONS TAB ========== */}
            <TabsContent value="communications" className="mt-0 space-y-4">
              {/* Composer */}
              <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-slate-300 text-sm uppercase tracking-wider">Send Message</h3>
                  <div className="flex gap-2">
                    <button onClick={() => setCommType("sms")} disabled={!contact.phone} className={`px-3 py-1 rounded text-sm ${commType === 'sms' ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-300'} ${!contact.phone ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      SMS
                    </button>
                    <button onClick={() => setCommType("email")} disabled={!contact.email} className={`px-3 py-1 rounded text-sm ${commType === 'email' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300'} ${!contact.email ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      Email
                    </button>
                  </div>
                </div>

                {commType === "email" && (
                  <Input value={commSubject} onChange={(e) => setCommSubject(e.target.value)} placeholder="Subject..." className="bg-slate-900 border-slate-700" />
                )}

                <Textarea value={commBody} onChange={(e) => setCommBody(e.target.value)} placeholder={commType === 'sms' ? 'Type your message...' : 'Compose your email...'} className="bg-slate-900 border-slate-700" rows={commType === 'sms' ? 3 : 6} data-testid="comm-body" />

                {commType === 'sms' && (
                  <div className="text-xs text-slate-500">{commBody.length}/160 {commBody.length > 160 && `(${Math.ceil(commBody.length / 160)} messages)`}</div>
                )}

                <div className="flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" disabled={aiDrafting} className="border-slate-600">
                        <Sparkles className={`h-4 w-4 mr-1 ${aiDrafting ? 'animate-pulse' : ''}`} />
                        {aiDrafting ? 'Drafting...' : 'AI Draft'}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-slate-900 border-slate-700">
                      <DropdownMenuItem onClick={() => handleAiDraft("initial")}>Initial Outreach</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAiDraft("follow_up")}>Follow Up</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAiDraft("appointment_reminder")}>Appointment Reminder</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAiDraft("estimate_follow_up")}>Estimate Follow Up</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAiDraft("reengagement")}>Re-engagement</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <div className="flex-1" />

                  <Button variant="outline" size="sm" onClick={handleLogCommunication} disabled={sending || !commBody.trim()} className="border-slate-600">
                    Log Only
                  </Button>
                  <Button size="sm" onClick={handleSendCommunication} disabled={sending || !commBody.trim()} className="bg-emerald-600 hover:bg-emerald-500" data-testid="send-comm-btn">
                    <Send className="h-4 w-4 mr-1" />
                    {sending ? 'Sending...' : 'Send'}
                  </Button>
                </div>
              </div>

              {/* Communications History */}
              <div className="space-y-2">
                <h3 className="font-medium text-slate-300 text-sm uppercase tracking-wider">History</h3>
                {loading ? (
                  <div className="text-center py-8 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
                ) : fullContact?.communications?.length > 0 ? (
                  fullContact.communications.map(comm => (
                    <div key={comm.comm_id} className={`p-3 rounded-lg border ${comm.direction === 'outbound' ? 'bg-slate-800/30 border-slate-700/50 ml-8' : 'bg-blue-900/20 border-blue-700/30 mr-8'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="border-slate-600 text-xs">
                            {comm.type?.toUpperCase()} {comm.direction === 'inbound' ? '← In' : '→ Out'}
                          </Badge>
                          {comm.ai_generated && <Badge className="bg-purple-500/20 text-purple-400 text-xs">AI</Badge>}
                        </div>
                        <span className="text-xs text-slate-500">{formatRelativeTime(comm.created_at)}</span>
                      </div>
                      {comm.subject && <div className="text-sm font-medium text-slate-300 mb-1">{comm.subject}</div>}
                      <p className="text-slate-300 text-sm whitespace-pre-wrap">{comm.body}</p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-slate-500">No communications yet</div>
                )}
              </div>
            </TabsContent>

            {/* ========== ESTIMATES TAB ========== */}
            <TabsContent value="estimates" className="mt-0 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-slate-300 text-sm uppercase tracking-wider">Linked Estimates</h3>
                <Button size="sm" onClick={() => window.location.href = `/estimates/new?contact_id=${contact.contact_id}`} className="bg-emerald-600 hover:bg-emerald-500">
                  <Plus className="h-4 w-4 mr-1" /> Create Estimate
                </Button>
              </div>

              {loading ? (
                <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-500" /></div>
              ) : fullContact?.estimates?.length > 0 ? (
                <div className="space-y-3">
                  {fullContact.estimates.map(est => (
                    <div key={est.estimate_id || est._id} className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-sm text-slate-300">{est.estimate_number || est.estimate_id}</span>
                        <Badge className={est.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-400' : est.status === 'sent' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-500/20 text-slate-400'}>
                          {est.status || 'draft'}
                        </Badge>
                      </div>
                      <div className="text-slate-400 text-sm">{est.service_type || 'General'}</div>
                      <div className="text-lg font-bold text-emerald-400 mt-1">{formatCurrency(est.total || est.grand_total)}</div>
                      <div className="text-xs text-slate-500 mt-1">Created {formatDate(est.created_at)}</div>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => window.location.href = `/estimates/${est.estimate_id || est._id}`} className="border-slate-600">
                          <ExternalLink className="h-3.5 w-3.5 mr-1" /> View
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>No estimates created yet</p>
                </div>
              )}
            </TabsContent>

            {/* ========== DOCUMENTS TAB ========== */}
            <TabsContent value="documents" className="mt-0 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-slate-300 text-sm uppercase tracking-wider">Documents</h3>
                <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-emerald-600 hover:bg-emerald-500">
                  <Upload className="h-4 w-4 mr-1" /> {uploading ? 'Uploading...' : 'Upload'}
                </Button>
                <input ref={fileInputRef} type="file" multiple hidden onChange={handleUpload} />
              </div>

              {loading ? (
                <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-500" /></div>
              ) : fullContact?.documents?.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {fullContact.documents.map(doc => (
                    <div key={doc.doc_id} className="bg-slate-800/50 rounded-lg border border-slate-700 p-3 flex items-center gap-3">
                      <div className="w-10 h-10 rounded bg-slate-700 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-slate-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-300 truncate">{doc.filename}</div>
                        <div className="text-xs text-slate-500">{doc.file_size_display || formatFileSize(doc.file_size)}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <a href={doc.file_url} download={doc.filename} className="p-1.5 hover:bg-slate-700 rounded">
                          <Download className="h-4 w-4 text-slate-400" />
                        </a>
                        <button onClick={() => handleDeleteDocument(doc.doc_id)} className="p-1.5 hover:bg-red-900/30 rounded">
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Upload className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>No documents uploaded yet</p>
                </div>
              )}
            </TabsContent>

            {/* ========== ACTIVITY TAB ========== */}
            <TabsContent value="activity" className="mt-0">
              <h3 className="font-medium text-slate-300 text-sm uppercase tracking-wider mb-4">Activity Timeline</h3>
              {loading ? (
                <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-500" /></div>
              ) : fullContact?.activity?.length > 0 ? (
                <div className="space-y-0">
                  {fullContact.activity.map((act, idx) => (
                    <div key={act.activity_id} className="flex items-start gap-3 py-3 border-b border-slate-800/50 last:border-0">
                      <div className="flex flex-col items-center">
                        <div className={`w-2.5 h-2.5 rounded-full ${act.action?.includes('won') ? 'bg-emerald-500' : act.action?.includes('lost') ? 'bg-red-500' : act.action?.includes('created') ? 'bg-blue-500' : 'bg-slate-600'}`} />
                        {idx < fullContact.activity.length - 1 && <div className="w-px h-full bg-slate-800 mt-1" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-300 text-sm">{act.description || act.action?.replace(/_/g, ' ')}</p>
                        <span className="text-xs text-slate-500">{formatRelativeTime(act.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <History className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>No activity yet</p>
                </div>
              )}
            </TabsContent>

            {/* ========== AI INSIGHTS TAB ========== */}
            <TabsContent value="ai" className="mt-0 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-slate-300 text-sm uppercase tracking-wider flex items-center gap-2">
                  <Brain className="h-4 w-4 text-purple-400" /> AI Analysis
                </h3>
                <Button size="sm" variant="outline" onClick={loadAiAnalysis} disabled={loadingAnalysis} className="border-slate-600">
                  <RefreshCw className={`h-4 w-4 mr-1 ${loadingAnalysis ? 'animate-spin' : ''}`} />
                  {loadingAnalysis ? 'Analyzing...' : 'Refresh'}
                </Button>
              </div>

              {loadingAnalysis ? (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-400 mb-3" />
                  <p className="text-slate-400">AI is analyzing this lead...</p>
                </div>
              ) : aiAnalysis ? (
                <div className="space-y-4">
                  {/* Conversion Probability */}
                  <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                    <h4 className="text-sm text-slate-400 mb-2">Conversion Probability</h4>
                    <div className="flex items-center gap-4">
                      <div className={`text-4xl font-bold ${aiAnalysis.conversion_probability >= 70 ? 'text-emerald-400' : aiAnalysis.conversion_probability >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                        {aiAnalysis.conversion_probability}%
                      </div>
                      <p className="text-slate-300 text-sm flex-1">{aiAnalysis.conversion_reasoning}</p>
                    </div>
                  </div>

                  {/* Suggested Actions */}
                  <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                    <h4 className="text-sm text-slate-400 mb-3">Suggested Next Actions</h4>
                    <div className="space-y-3">
                      {aiAnalysis.suggested_actions?.map((action, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx === 0 ? 'bg-emerald-600' : idx === 1 ? 'bg-blue-600' : 'bg-slate-600'}`}>
                            {action.priority}
                          </div>
                          <div>
                            <div className="text-slate-200 font-medium">{action.action}</div>
                            <div className="text-slate-500 text-sm">{action.reasoning}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Risk & Positive */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-red-900/20 rounded-lg border border-red-700/30 p-4">
                      <h4 className="text-sm text-red-400 mb-2 flex items-center gap-1"><XCircle className="h-4 w-4" /> Risk Factors</h4>
                      <ul className="space-y-1">
                        {aiAnalysis.risk_factors?.map((risk, idx) => (
                          <li key={idx} className="text-slate-300 text-sm">• {risk}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-emerald-900/20 rounded-lg border border-emerald-700/30 p-4">
                      <h4 className="text-sm text-emerald-400 mb-2 flex items-center gap-1"><CheckCircle className="h-4 w-4" /> Positive Signals</h4>
                      <ul className="space-y-1">
                        {aiAnalysis.positive_signals?.map((signal, idx) => (
                          <li key={idx} className="text-slate-300 text-sm">• {signal}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Timeline & Confidence */}
                  <div className="flex gap-4">
                    <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 flex-1">
                      <h4 className="text-xs text-slate-500 uppercase">Recommended Close By</h4>
                      <p className="text-slate-200 mt-1">{aiAnalysis.recommended_timeline}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 flex-1">
                      <h4 className="text-xs text-slate-500 uppercase">Value Confidence</h4>
                      <Badge className={`mt-1 ${aiAnalysis.estimated_value_confidence === 'high' ? 'bg-emerald-500/20 text-emerald-400' : aiAnalysis.estimated_value_confidence === 'medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-500/20 text-slate-400'}`}>
                        {aiAnalysis.estimated_value_confidence}
                      </Badge>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Brain className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>Click Refresh to generate AI analysis</p>
                </div>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDetail;
